import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  SwapHoriz,
  Receipt,
  Person,
  Home,
  AttachMoney,
  Business,
  CheckCircle,
} from '@mui/icons-material';

const ChangePaytmentPlan = () => {
  const canWriteChangePlan = hasWritePermission('plot-booking.change-payment-plan');
  const [bookingId, setBookingId] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [newPaymentPlan, setNewPaymentPlan] = useState('');
  const [errors, setErrors] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Sample booking data - this would come from API
  const [bookingData, setBookingData] = useState({
    customerName: 'John Doe',
    associateId: 'ASC001',
    associateName: 'Jane Smith',
    plot: 'A-101',
    plotSize: '1200 Sq Ft',
    plotStatus: 'Booked',
    bookingId: 'BK001',
    bookingDate: '2024-01-15',
    paidAmount: '₹5,00,000',
    plotAmount: '₹15,00,000',
    currentPlan: 'Standard EMI Plan',
  });

  const handleBookingSearch = async () => {
    if (!bookingId.trim()) {
      setErrors({ bookingId: 'Please enter a booking ID' });
      return;
    }

    setSearchLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      setShowDetails(true);
      setBookingData({
        ...bookingData,
        bookingId: bookingId,
      });
      setSuccessMessage('');
      console.log('Searching for booking:', bookingId);
      setSearchLoading(false);
    }, 1500);
  };

  const handleSubmitPayment = async () => {
    if (!newPaymentPlan) {
      setErrors({ newPaymentPlan: 'Please select a new payment plan' });
      return;
    }

    setSubmitLoading(true);

    // Simulate API call
    setTimeout(() => {
      setSuccessMessage(`Payment plan successfully changed to: ${newPaymentPlan}`);
      setShowDetails(false);
      setBookingId('');
      setNewPaymentPlan('');
      setErrors({});
      console.log('Submitting payment plan change:', {
        bookingId,
        newPaymentPlan,
        bookingData,
      });
      setSubmitLoading(false);
    }, 2000);
  };

  const handleBookingIdChange = (e) => {
    setBookingId(e.target.value);
    if (errors.bookingId) {
      setErrors({ ...errors, bookingId: '' });
    }
  };

  const handlePaymentPlanChange = (e) => {
    setNewPaymentPlan(e.target.value);
    if (errors.newPaymentPlan) {
      setErrors({ ...errors, newPaymentPlan: '' });
    }
  };

  const paymentPlanOptions = [
    'Standard EMI Plan',
    'Quarterly Payment Plan',
    'Half-Yearly Payment Plan',
    'Annual Payment Plan',
    'One-Time Payment',
    'Flexible EMI Plan',
  ];

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Change Payment Plan
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search and modify payment plans for existing bookings
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Search sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Booking
            </Typography>
          </Box>

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Booking ID"
                value={bookingId}
                onChange={handleBookingIdChange}
                placeholder="Enter Booking ID"
                variant="outlined"
                error={!!errors.bookingId}
                helperText={errors.bookingId}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleBookingSearch}
                disabled={searchLoading}
                startIcon={<Search />}
                sx={{
                  height: 56,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2
                }}
              >
                {searchLoading ? 'Searching...' : 'Search Booking'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Booking Details */}
      {showDetails && (
        <Box>
          {/* Customer Information */}
          <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Person sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#000' }}>
                  Customer Information
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Customer Name</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                      {bookingData.customerName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {bookingData.bookingId}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Associate ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {bookingData.associateId}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Associate Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {bookingData.associateName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Booking Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {bookingData.bookingDate}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Status</Typography>
                    <Chip
                      label={bookingData.plotStatus}
                      color={bookingData.plotStatus === 'Booked' ? 'warning' : 'success'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Plot Information */}
          <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Home sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#000' }}>
                  Plot Information
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Number</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                      {bookingData.plot}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Size</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                      {bookingData.plotSize}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Plot Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      {bookingData.plotAmount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {bookingData.paidAmount}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Payment Plan Change */}
          <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, border: '2px solid #1976d2' }}>
            <CardContent sx={{ p: 3, backgroundColor: '#f3f7ff' }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <SwapHoriz sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  Change Payment Plan
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Current Payment Plan</Typography>
                    <Chip
                      label={bookingData.currentPlan}
                      color="info"
                      sx={{ fontWeight: 600, fontSize: '14px' }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.newPaymentPlan}>
                    <InputLabel>New Payment Plan *</InputLabel>
                    <Select
                      value={newPaymentPlan}
                      label="New Payment Plan *"
                      onChange={handlePaymentPlanChange}
                      sx={{ borderRadius: 2 }}
                    >
                      {paymentPlanOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.newPaymentPlan && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {errors.newPaymentPlan}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <AttachMoney sx={{ color: '#f57c00', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#000' }}>
                  Financial Summary
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Box textAlign="center" sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#000' }}>
                      {bookingData.plotAmount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center" sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {bookingData.paidAmount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center" sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">Balance Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#f57c00' }}>
                      ₹10,00,000
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => setShowDetails(false)}
              sx={{
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmitPayment}
              disabled={!newPaymentPlan || submitLoading}
              startIcon={<SwapHoriz />}
              sx={{
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '15px',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                borderRadius: 2
              }}
            >
              {submitLoading ? 'Updating...' : 'Update Payment Plan'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ChangePaytmentPlan;